@if (initialized) {
    <div class="cn-import-start">

        <!-- ===================== 1) GESELLSCHAFT + IMPORT-METHODE ===================== -->
        <div class="surface-0 border-1 surface-border border-round p-3 mb-3">
            <div class="text-lg font-medium mb-3">Provisions-Import starten</div>

            <div class="grid align-items-end">
                <!-- Gesellschaft -->
                <div class="col-12 md:col-6">
                    <label class="text-sm text-600" for="cn-import-partner">Gesellschaft (Pflicht)</label>
                    @if (partnerOptionsLoading) {
                        <p-skeleton height="2.5rem" styleClass="w-full mt-1"></p-skeleton>
                    } @else {
                        <p-select id="cn-import-partner"
                                  [(ngModel)]="selectedPartnerId"
                                  [options]="partnerOptions"
                                  optionLabel="label" optionValue="value"
                                  [filter]="true" filterBy="label"
                                  placeholder="Gesellschaft wählen"
                                  emptyMessage="Keine Gesellschaften konfiguriert"
                                  emptyFilterMessage="Keine Gesellschaft gefunden"
                                  styleClass="w-full"
                                  (onChange)="onSelectionChanged()">
                        </p-select>
                    }
                </div>

                <!-- Import-Methode -->
                <div class="col-12 md:col-6">
                    <label class="text-sm text-600" for="cn-import-method">Import-Methode</label>
                    <p-select id="cn-import-method"
                              [(ngModel)]="selectedUploadMethod"
                              [options]="uploadMethodOptions"
                              optionLabel="label" optionValue="value"
                              placeholder="Import-Methode wählen"
                              styleClass="w-full"
                              (onChange)="onSelectionChanged()">
                    </p-select>
                </div>
            </div>

            <!-- Lokaler KI-Agent: Prompt generieren -->
            @if (isLocalAiAgent()) {
                <div class="flex justify-content-end mt-3">
                    <p-button label="Prompt generieren"
                              icon="fa-regular fa-wand-magic-sparkles"
                              [disabled]="!canGeneratePrompt()"
                              [loading]="promptState === 'busy'"
                              (onClick)="generatePrompt()">
                    </p-button>
                </div>
                @if (selectedPartnerId == null) {
                    <div class="text-600 text-sm mt-2">
                        Wählen Sie eine Gesellschaft, um den Prompt für Ihren lokalen KI-Agenten zu erzeugen.
                    </div>
                }
            }

            <!-- Strukturierte Datei / Manuell: Platzhalter -->
            @if (isPlaceholderMethod()) {
                <p-message severity="info" class="block mt-3" text="Dieser Import-Pfad folgt."></p-message>
            }
        </div>

        <!-- ===================== 2) AI-AGENT-PROMPT-ERGEBNIS ===================== -->
        @if (isLocalAiAgent()) {

            <!-- Busy -->
            @if (promptState === 'busy') {
                <div class="surface-0 border-1 surface-border border-round p-3 mb-3">
                    <div class="text-sm text-600 mb-2">Prompt wird erzeugt…</div>
                    <p-skeleton height="2rem" styleClass="w-6 mb-2"></p-skeleton>
                    <p-skeleton height="16rem" styleClass="w-full"></p-skeleton>
                </div>
            }

            <!-- Error -->
            @if (promptState === 'error') {
                <div class="surface-0 border-1 surface-border border-round p-3 mb-3">
                    <p-message severity="error" class="block" [text]="promptError"></p-message>
                </div>
            }

            <!-- Ready -->
            @if (promptState === 'ready' && promptResult) {
                <div class="surface-0 border-1 surface-border border-round p-3 mb-3">

                    <!-- Hinweis: kein aktives Importprofil hinterlegt -->
                    @if (!promptResult.profileConfigured) {
                        <p-message severity="warn" class="block mb-3"
                                   text="Für diese Gesellschaft ist noch kein aktives Importprofil hinterlegt.">
                        </p-message>
                    }

                    <!-- Konfigurations-Übersicht (read-only) -->
                    <div class="text-lg font-medium mb-3">Konfigurations-Übersicht</div>
                    <div class="surface-50 border-round p-3 mb-3">
                        <div class="grid">
                            <div class="col-12 md:col-6">
                                <div class="text-xs text-600">Gesellschaft</div>
                                <div class="font-medium">{{ promptResult.partnerName || '–' }}</div>
                            </div>
                            <div class="col-12 md:col-6">
                                <div class="text-xs text-600">Quellformat</div>
                                <div class="font-medium">{{ promptResult.sourceFormat || '–' }}</div>
                            </div>
                            <div class="col-12 md:col-6">
                                <div class="text-xs text-600">VSNR-Präfix (Strip-Regex)</div>
                                <div class="font-medium font-mono text-sm">
                                    {{ promptResult.vsnrPrefixStripRegex || '–' }}
                                </div>
                            </div>
                            <div class="col-12">
                                <div class="text-xs text-600">Agenten-Anweisungen</div>
                                <div class="text-sm cn-import-pre-line">
                                    {{ promptResult.agentInstructions || '–' }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Prompt (read-only, kopierbar) -->
                    <div class="flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                        <div class="text-lg font-medium">Prompt für den lokalen KI-Agenten</div>
                        <div class="flex align-items-center gap-2">
                            @if (copyConfirmed) {
                                <span class="text-green-600 text-sm font-medium">
                                    <i class="fa-regular fa-circle-check mr-1"></i>Kopiert!
                                </span>
                            }
                            <p-button label="In Zwischenablage kopieren"
                                      icon="fa-regular fa-copy"
                                      size="small"
                                      severity="secondary"
                                      [disabled]="!promptResult.prompt"
                                      (onClick)="copyPrompt()">
                            </p-button>
                        </div>
                    </div>
                    <textarea pTextarea
                              class="w-full cn-import-prompt text-sm"
                              rows="16"
                              [readOnly]="true"
                              [ngModel]="promptResult.prompt">
                    </textarea>
                    <div class="text-xs text-600 mt-2">
                        Kopieren Sie diesen Prompt und übergeben Sie ihn Ihrem lokalen KI-Agenten.
                    </div>
                </div>
            }

            <!-- Idle -->
            @if (promptState === 'idle') {
                <div class="surface-0 border-1 surface-border border-round p-3 mb-3">
                    <div class="text-center text-600 p-4">
                        Wählen Sie eine Gesellschaft und erzeugen Sie den Prompt für Ihren lokalen KI-Agenten.
                    </div>
                </div>
            }
        }

    </div>
}
